import { useEffect } from "react";
import { supabase } from "./lib/supabase";
import { notifySaved } from "./notifications";

const METHODS=["VT","LPI / PT","MPI / MT","UT","RT","PAUT","TOFD"];
const tidy=(value:string|null|undefined)=>(value||"").replace(/\s+/g," ").trim();

async function projectContext(){
  const head=document.querySelector<HTMLElement>(".project-detail-head");
  if(!head)return null;
  const number=tidy(head.querySelector(".eyebrow")?.textContent);
  const name=tidy(head.querySelector("h1")?.textContent);
  let query=supabase.from("projects").select("id,company_id").limit(1);
  if(number&&number!=="PROJECT")query=query.eq("project_number",number);
  else if(name)query=query.eq("name",name);
  else return null;
  const {data}=await query.maybeSingle();
  return data as {id:string;company_id:string}|null;
}

function labelByText(form:HTMLFormElement,text:string){
  return [...form.querySelectorAll<HTMLLabelElement>("label")].find(label=>tidy(label.childNodes[0]?.textContent||label.textContent).toLowerCase().startsWith(text.toLowerCase()))||null;
}

async function uploadInspectionFile(companyId:string,projectId:string,file:File){
  const safe=file.name.replace(/[^a-zA-Z0-9._-]/g,"_");
  const path=`${companyId}/${projectId}/inspections/${Date.now()}-${safe}`;
  const result=await supabase.storage.from("project-documents").upload(path,file,{contentType:file.type||undefined});
  if(result.error)throw result.error;
  return path;
}

function refreshInspectionTab(){
  const picker=document.querySelector<HTMLSelectElement>("#project-section");
  if(!picker)return;
  picker.value="overview";
  picker.dispatchEvent(new Event("change",{bubbles:true}));
  window.setTimeout(()=>{
    picker.value="inspections";
    picker.dispatchEvent(new Event("change",{bubbles:true}));
  },60);
}

async function enhanceInspectionForm(form:HTMLFormElement){
  if(form.dataset.arcvaultInspectionLinked)return;
  const title=tidy(form.querySelector("h2")?.textContent).toLowerCase();
  if(title!=="add inspection record")return;
  form.dataset.arcvaultInspectionLinked="true";

  const context=await projectContext();
  if(!context)return;
  const {data:welds}=await supabase.from("project_weld_register")
    .select("id,weld_number,joint_id,drawing_id,drawing_number,process,wpds_number,welder_name")
    .eq("project_id",context.id)
    .order("weld_number");

  const grid=form.querySelector<HTMLElement>(".form-grid");
  if(!grid)return;

  const weldLabel=document.createElement("label");
  weldLabel.dataset.arcvaultWeldLink="true";
  weldLabel.append("Weld / Joint");
  const weldSelect=document.createElement("select");
  weldSelect.innerHTML='<option value="">Project-level / no specific weld</option>';
  (welds||[]).forEach((w:any)=>{
    const option=document.createElement("option");
    option.value=w.id;
    option.textContent=`${w.joint_id||w.weld_number} · ${w.drawing_number||"No drawing"} · ${w.process||"Process pending"}${w.wpds_number?` · ${w.wpds_number}`:""}`;
    option.dataset.drawingId=w.drawing_id||"";
    weldSelect.append(option);
  });
  weldLabel.append(weldSelect);

  const recordType=labelByText(form,"Record type");
  if(recordType)recordType.insertAdjacentElement("afterend",weldLabel);else grid.prepend(weldLabel);

  weldSelect.addEventListener("change",()=>{
    const drawing=labelByText(form,"Drawing")?.querySelector<HTMLSelectElement>("select");
    const option=weldSelect.selectedOptions[0];
    if(drawing&&option?.dataset.drawingId){drawing.value=option.dataset.drawingId;drawing.dispatchEvent(new Event("change",{bubbles:true}))}
  });

  const methodLabel=labelByText(form,"Method");
  const methodInput=methodLabel?.querySelector<HTMLInputElement>("input");
  if(methodLabel&&methodInput){
    methodInput.style.display="none";
    const picker=document.createElement("div");
    picker.className="ndt-method-picker arc-inspection-methods";
    const selected=new Set((methodInput.value||"VT").split(",").map(tidy).filter(Boolean));
    const sync=()=>{methodInput.value=[...selected].join(", ");methodInput.dispatchEvent(new Event("input",{bubbles:true}))};
    METHODS.forEach(method=>{
      const button=document.createElement("button");
      button.type="button";
      const paint=()=>{const on=selected.has(method);button.classList.toggle("active",on);button.textContent=`${on?"✓ ":""}${method}`};
      button.addEventListener("click",()=>{selected.has(method)?selected.delete(method):selected.add(method);paint();sync()});
      paint();picker.append(button);
    });
    methodLabel.append(picker);
    sync();
  }

  form.addEventListener("submit",async event=>{
    event.preventDefault();
    event.stopImmediatePropagation();
    const saveButton=form.querySelector<HTMLButtonElement>(".modal-actions .primary");
    if(saveButton?.disabled)return;
    if(saveButton){saveButton.disabled=true;saveButton.textContent="Saving…"}
    let message=form.querySelector<HTMLElement>(".form-message");
    if(!message){message=document.createElement("div");message.className="form-message";form.querySelector(".modal-actions")?.before(message)}
    message.textContent="";
    try{
      const {data:{user}}=await supabase.auth.getUser();
      if(!user)throw new Error("Your session has expired.");
      const type=labelByText(form,"Record type")?.querySelector<HTMLSelectElement>("select")?.value||"ndt_report";
      const number=labelByText(form,"Record number")?.querySelector<HTMLInputElement>("input")?.value.trim()||"";
      const date=labelByText(form,"Inspection date")?.querySelector<HTMLInputElement>("input")?.value||"";
      const method=labelByText(form,"Method")?.querySelector<HTMLInputElement>("input")?.value.trim()||"";
      const result=labelByText(form,"Result")?.querySelector<HTMLSelectElement>("select")?.value||"pending";
      const inspector=labelByText(form,"Inspector")?.querySelector<HTMLInputElement>("input")?.value.trim()||"";
      const drawing=labelByText(form,"Drawing")?.querySelector<HTMLSelectElement>("select")?.value||"";
      const notes=labelByText(form,"Notes")?.querySelector<HTMLTextAreaElement>("textarea")?.value.trim()||"";
      const file=labelByText(form,"Report or file")?.querySelector<HTMLInputElement>('input[type="file"]')?.files?.[0]||null;
      if(!number)throw new Error("Enter a record number.");
      if(!date)throw new Error("Enter the inspection date.");
      const path=file?await uploadInspectionFile(context.company_id,context.id,file):null;
      const insert=await supabase.from("inspection_records").insert({
        company_id:context.company_id,project_id:context.id,weld_id:weldSelect.value||null,drawing_id:drawing||null,
        record_type:type,record_number:number,inspection_date:date,method:method||null,result,
        inspector:inspector||null,notes:notes||null,file_name:file?.name||null,file_path:path,created_by:user.id
      }).select().single();
      if(insert.error)throw insert.error;

      if(weldSelect.value&&type==="ndt_report"){
        const current=await supabase.from("project_weld_register").select("ndt_methods").eq("id",weldSelect.value).maybeSingle();
        const existing=Array.isArray(current.data?.ndt_methods)?current.data.ndt_methods:[];
        const chosen=method.split(",").map(tidy).filter(Boolean);
        const merged=[...new Set([...existing,...chosen])];
        const ndtStatus=result==="accepted"||result==="released"?"accepted":result==="rejected"?"rejected":"pending";
        await supabase.from("project_weld_register").update({ndt_methods:merged,ndt_status:ndtStatus,updated_at:new Date().toISOString()}).eq("id",weldSelect.value);
      }

      notifySaved(`${number} inspection record saved`);
      form.querySelector<HTMLButtonElement>(".close")?.click();
      refreshInspectionTab();
    }catch(error:any){
      message!.textContent=error?.message||"Could not save inspection record.";
      if(saveButton){saveButton.disabled=false;saveButton.textContent="Save record"}
    }
  },true);
}

async function decorateInspectionRows(){
  const picker=document.querySelector<HTMLSelectElement>("#project-section");
  if(!picker||picker.value!=="inspections")return;
  const context=await projectContext();
  if(!context)return;
  const {data,error}=await supabase.from("inspection_records")
    .select("record_number,weld_id,project_weld_register(weld_number,joint_id,drawing_number,process)")
    .eq("project_id",context.id);
  if(error)return;
  const byNumber=new Map((data||[]).map((row:any)=>[row.record_number,row]));
  document.querySelectorAll<HTMLElement>(".record-list article").forEach(article=>{
    if(article.dataset.arcvaultInspectionDecorated)return;
    const number=tidy(article.querySelector("div b")?.textContent);
    const row:any=byNumber.get(number);
    const weld=row?.project_weld_register;
    if(!row?.weld_id||!weld)return;
    const info=document.createElement("small");
    info.className="arc-linked-weld";
    info.textContent=`Weld / Joint ${weld.joint_id||weld.weld_number} · ${weld.drawing_number||"No drawing"} · ${weld.process||"Process pending"}`;
    article.querySelector("div")?.append(info);
    article.dataset.arcvaultInspectionDecorated="true";
  });
}

async function enhanceWeldRegisterVt(form:HTMLFormElement){
  const field=form.querySelector<HTMLElement>(".ndt-method-field .ndt-method-picker");
  if(!field||field.querySelector('[data-arc-vt="true"]'))return;
  const title=tidy(form.querySelector("h2")?.textContent).toLowerCase();
  if(title!=="add weld to register"&&title!=="edit weld record")return;
  const button=document.createElement("button");
  button.type="button";
  button.dataset.arcVt="true";
  button.textContent="VT";
  field.prepend(button);

  const jointInput=labelByText(form,"Weld / Joint ID")?.querySelector<HTMLInputElement>("input");
  const context=await projectContext();
  if(context&&jointInput?.value){
    const {data}=await supabase.from("project_weld_register").select("ndt_methods").eq("project_id",context.id).or(`joint_id.eq.${jointInput.value},weld_number.eq.${jointInput.value}`).limit(1).maybeSingle();
    if(Array.isArray(data?.ndt_methods)&&data.ndt_methods.includes("VT")){button.classList.add("active");button.textContent="✓ VT"}
  }
  button.addEventListener("click",()=>{const on=!button.classList.contains("active");button.classList.toggle("active",on);button.textContent=on?"✓ VT":"VT"});

  form.addEventListener("submit",()=>{
    const joint=jointInput?.value.trim();
    const wantVt=button.classList.contains("active");
    if(!joint)return;
    window.setTimeout(async()=>{
      if(form.isConnected)return;
      const ctx=await projectContext();if(!ctx)return;
      const {data}=await supabase.from("project_weld_register").select("id,ndt_methods,ndt_status").eq("project_id",ctx.id).or(`joint_id.eq.${joint},weld_number.eq.${joint}`).limit(1).maybeSingle();
      if(!data?.id)return;
      const methods=Array.isArray(data.ndt_methods)?[...data.ndt_methods]:[];
      const has=methods.includes("VT");
      if(wantVt&&!has)methods.unshift("VT");
      if(!wantVt&&has)methods.splice(methods.indexOf("VT"),1);
      await supabase.from("project_weld_register").update({ndt_methods:methods,ndt_status:methods.length?(data.ndt_status==="not_required"?"pending":data.ndt_status):"not_required",updated_at:new Date().toISOString()}).eq("id",data.id);
    },500);
  },true);
}

export default function InspectionWeldIntegration(){
  useEffect(()=>{
    let timer:number|undefined;
    const apply=()=>{
      window.clearTimeout(timer);
      timer=window.setTimeout(()=>{
        document.querySelectorAll<HTMLFormElement>("form.modal.wide").forEach(form=>{
          void enhanceInspectionForm(form);
          void enhanceWeldRegisterVt(form);
        });
        void decorateInspectionRows();
      },80);
    };
    const observer=new MutationObserver(apply);
    observer.observe(document.body,{childList:true,subtree:true});
    document.addEventListener("change",apply,true);
    apply();
    return()=>{window.clearTimeout(timer);observer.disconnect();document.removeEventListener("change",apply,true)};
  },[]);
  return null;
}
